/**
 * What a member opens once the first day is behind them — the other half of
 * `FirstDay.tsx`, and the reason that page is not a checklist somebody dismisses.
 *
 * The three acts are worth reading exactly once. A machine is linked and a
 * workspace is joined, and from then on "Install Airlock and link this machine"
 * is a numbered instruction over the top of a page somebody opens to see where
 * they work — a welcome screen that has outstayed the welcome.
 *
 * So this is the same facts, asked as the settled question. Not *what do I do
 * first*, but *where do I work, on what machines, and what does a new one of
 * them get* — the last because the day a member sets up a second machine is the
 * day they need that paragraph again, and it is the one thing here they cannot
 * work out by looking.
 *
 * Nothing new is read: everything is handed down from `FirstDay`, which holds
 * the four reads. A page that decided for itself would be a fifth read of the
 * same rows, answered a moment later, and the two would disagree on screen.
 */
import type React from 'react';
import { color } from '../../theme/tokens';
import { Note } from '../../components/list';
import { RowAction } from '../../components/primitives';
import { HairList, HairRow, SectionHead } from '../../components/Section';
import { since } from '../../lib/format';
import { sentenceList } from './parts';
import type { PageId } from '../../components/nav';
import type { Organization, WorkspaceSummary } from '../../lib/types';

export function Settled({
  organization,
  workspaces,
  joinedAt,
  linked,
  handed,
  onGo
}: {
  organization: Organization;
  /** Every workspace this member can reach, joined or not. */
  workspaces: WorkspaceSummary[];
  /** When they last joined one, by workspace id. Absent means never. */
  joinedAt: Map<string, string>;
  /** Machines of theirs holding a token that has not been revoked. */
  linked: number;
  /** What this organization sets a new install of theirs up with, key by key. */
  handed: string[];
  onGo: (page: PageId, section?: string | null) => void;
}): React.JSX.Element {
  // Joined first and by when, because that is the order of the question: the
  // one somebody came back to is the one they touched last. The rest keep the
  // list they arrive in, and are counted rather than drawn — a settled page
  // that listed everything shared with the organization would be the
  // Workspaces page with a different title.
  const mine = workspaces
    .filter((workspace) => joinedAt.has(workspace.id))
    .sort((a, b) => (joinedAt.get(b.id) ?? '').localeCompare(joinedAt.get(a.id) ?? ''));
  const waiting = workspaces.length - mine.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, padding: '22px var(--al-gutter) 30px' }}>
      <section aria-labelledby="settled-where">
        <SectionHead
          id="settled-where"
          title="Where you work"
          description={`Your workspaces in ${organization.name}, the ones you last joined first.`}
          link={<RowAction onClick={() => onGo('workspaces')}>All of them</RowAction>}
        />
        <HairList label="Your workspaces">
          {mine.map((workspace) => (
            <HairRow
              key={workspace.id}
              mark={{ dot: workspace.color }}
              title={workspace.name}
              description={`${workspace.projects} ${workspace.projects === 1 ? 'repository' : 'repositories'} · ${workspace.access.right} · ${workspace.access.via}`}
              end={`joined ${since(joinedAt.get(workspace.id) ?? null)}`}
            />
          ))}
        </HairList>
        {waiting > 0 && (
          <div style={{ marginTop: 16 }}>
            <Note>
              {waiting === 1 ? 'One more workspace is' : `${waiting} more workspaces are`} shared with you and not joined
              on any machine of yours. Joining is done in the app, one at a time — you pick the folder.
            </Note>
          </div>
        )}
      </section>

      <section aria-labelledby="settled-machines">
        <SectionHead
          id="settled-machines"
          title={linked === 1 ? 'One machine of yours is linked' : `${linked} machines of yours are linked`}
          description="Each one pulls what this organization publishes, until you unlink it."
          link={<RowAction onClick={() => onGo('machines')}>Machines</RowAction>}
        />
        {handed.length > 0 && (
          <Note>
            <span style={{ color: color.ink }}>Setting up another one?</span> {organization.name} hands it{' '}
            {sentenceList(handed)} at its first link — never a credential. Those you sign in to yourself, on the machine,
            as you did on this one.
          </Note>
        )}
      </section>
    </div>
  );
}
